/**
 * Sumur mana yang dibuka lebih dulu ketika sebuah proyek dibuka.
 *
 * Ukuran yang dipakai sebelumnya adalah "sumur pertama yang punya kurva", dan
 * ukuran itu memilih sumur yang salah pada kumpulan data yang mnemonic-nya
 * bukan mnemonic masa kini. Teapot Dome membukanya pada 1-Tp-3, sebuah sumur
 * tera lama yang membawa ASN, GRN, LAT, LN, NEUT, dan SPR — enam kurva, jadi
 * lolos ujian "punya kurva", tetapi tidak satu pun diminta oleh lima track
 * bawaan. Yang tersaji karena itu adalah lembar log kosong pada proyek yang
 * 922 dari 1.402 sumurnya membawa kurva yang memang digambar susunan bawaan,
 * 746 di antaranya GR.
 *
 * Kegagalan seperti itu tidak bersuara: tidak ada galat, tidak ada bingkai
 * merah, hanya trek putih yang terbaca sebagai "datanya tidak ada" alih-alih
 * "sumur ini bukan yang seharusnya dibuka". Maka ukurannya dinaikkan menjadi
 * "punya kurva yang tergambar" — sumur yang setidaknya satu mnemonic-nya
 * diminta oleh susunan bawaan.
 */
import type { Well } from "../types";
import { defaultTracks } from "./tracklayout";

/** Mnemonic yang diminta oleh susunan track bawaan. */
export function plottableMnemonics(): Set<string> {
  return new Set(
    defaultTracks().flatMap((track) => track.curves.map((curve) => curve.name.toUpperCase())),
  );
}

function hasPlottableCurve(well: Well, plottable: Set<string>): boolean {
  return well.curve_sets.some((set) =>
    set.mnemonics.some((mnemonic) => plottable.has(mnemonic.toUpperCase())),
  );
}

function hasAnyCurve(well: Well): boolean {
  return well.curve_sets.some((set) => set.curve_count > 0);
}

/**
 * Pilihan sumur pembuka: yang terakhir dipakai, lalu yang pertama tergambar,
 * lalu yang pertama berkurva, lalu yang pertama saja.
 *
 * Dua langkah terakhir tetap ada supaya proyek yang seluruh mnemonic-nya di
 * luar susunan bawaan — pohon proyeknya masih terisi, kurvanya masih bisa
 * ditarik ke track sendiri — tidak jatuh ke sumur tanpa data sama sekali.
 */
export function pickDefaultWell(wells: Well[], remembered?: number): Well | null {
  if (!wells.length) return null;
  const plottable = plottableMnemonics();
  return (
    wells.find((well) => well.id === remembered) ??
    wells.find((well) => hasPlottableCurve(well, plottable)) ??
    wells.find(hasAnyCurve) ??
    wells[0]
  );
}
